import { useLayoutEffect, useState } from 'react';

/**
 * The `open` value to give a Headless UI `<Dialog>` that is always mounted.
 *
 * YOJO-3393 [shared Dialog leaves the page behind it reachable by keyboard and screen readers],
 * YOJO-3395 [4 dialogs built directly on Headless UI leave the page behind reachable]:
 * Headless UI hides the rest of the page (aria-hidden + inert) only if it already knows where the
 * page is when the dialog opens, and it finds the page one render AFTER the Dialog mounts. So the
 * Dialog stays mounted (no Transition / Transition.Root unmounting it while closed), and `open` is held
 * false until after the first render, which also covers callers that mount the dialog already open.
 * useLayoutEffect (not useEffect) flips `mounted` before the browser paints, so a dialog mounted
 * already open never shows a closed frame first (e.g. the CapabilityPackagesAdmin editor-to-shrink
 * swap, the SleepAnalytics explainer).
 *
 * With `open` given, Headless UI wraps the dialog in its own Transition, which drives the
 * TransitionChild enter/leave animations inside it.
 */
export function useDialogOpen(isOpen: boolean): boolean {
  const [mounted, setMounted] = useState(false);
  useLayoutEffect(() => {
    setMounted(true);
  }, []);
  return isOpen && mounted;
}
